// Pure dividend projection. All compounding comes from share-price growth,
// dividend growth, and reinvested dividends — no fixed interest rate.

export interface DividendCalcInput {
  initialInvestment: number;
  monthlyContribution: number;
  startingYieldPct: number; // e.g. 4 means 4%
  dividendGrowthPct: number; // annual % growth in dividend per share
  priceGrowthPct: number; // annual % growth in share price
  years: number;
  reinvest: boolean;
  taxRate: number; // 0..1 applied to gross dividends
}

export interface YearRow {
  year: number;
  contributed: number;
  portfolioValue: number;
  grossDividends: number;
  netDividends: number;
  cumulativeNetDividends: number;
  yieldOnCost: number; // % vs total contributed
}

export interface DividendCalcResult {
  rows: YearRow[];
  totalContributed: number;
  finalValue: number;
  totalGrossDividends: number;
  totalNetDividends: number;
}

export function projectDividends(input: DividendCalcInput): DividendCalcResult {
  const {
    initialInvestment,
    monthlyContribution,
    startingYieldPct,
    dividendGrowthPct,
    priceGrowthPct,
    reinvest,
    taxRate,
  } = input;

  // Safety: clamp horizon. UI caps at 50; engine hard-caps at 100 so a
  // pasted URL or future feature cannot trigger a runaway render.
  const years = Math.min(Math.max(Math.floor(input.years || 0), 1), 100);

  // Anchor a synthetic share price at 100 for clarity. Math is unit-free.
  let price = 100;
  let dividendPerShare = price * (startingYieldPct / 100);
  let shares = initialInvestment / price;

  const priceG = priceGrowthPct / 100;
  const divG = dividendGrowthPct / 100;
  const annualContribution = monthlyContribution * 12;

  let totalContributed = initialInvestment;
  let cumulativeNet = 0;
  let totalGross = 0;
  let totalNet = 0;
  const rows: YearRow[] = [];

  for (let y = 1; y <= years; y++) {
    const avgPrice = price * (1 + priceG / 2);
    const contribShares = annualContribution / avgPrice;
    shares += contribShares;
    totalContributed += annualContribution;

    price = price * (1 + priceG);
    dividendPerShare = dividendPerShare * (1 + divG);

    const gross = shares * dividendPerShare;
    const net = gross * (1 - taxRate);

    if (reinvest) {
      shares += net / price;
    }

    totalGross += gross;
    totalNet += net;
    cumulativeNet += net;

    const portfolioValue = shares * price;
    const yieldOnCost = totalContributed > 0 ? (gross / totalContributed) * 100 : 0;

    rows.push({
      year: y,
      contributed: totalContributed,
      portfolioValue,
      grossDividends: gross,
      netDividends: net,
      cumulativeNetDividends: cumulativeNet,
      yieldOnCost,
    });
  }

  return {
    rows,
    totalContributed,
    finalValue: rows.length ? rows[rows.length - 1].portfolioValue : initialInvestment,
    totalGrossDividends: totalGross,
    totalNetDividends: totalNet,
  };
}

export function toCSV(rows: YearRow[]): string {
  const header = [
    "Year",
    "Contributed",
    "PortfolioValue",
    "GrossDividends",
    "NetDividends",
    "CumulativeNetDividends",
    "YieldOnCost%",
  ].join(",");
  const body = rows
    .map((r) =>
      [
        r.year,
        r.contributed.toFixed(2),
        r.portfolioValue.toFixed(2),
        r.grossDividends.toFixed(2),
        r.netDividends.toFixed(2),
        r.cumulativeNetDividends.toFixed(2),
        r.yieldOnCost.toFixed(2),
      ].join(",")
    )
    .join("\n");
  return `${header}\n${body}`;
}
